// ============================================================
// CreditNoteView.tsx — B3b: a credit note opened in the MAIN panel, like a
// bill (BillView renders it when a credit note is opened — from the bill's
// "£X credited: CN-…" links, its Payments & credits card, or the case's
// bills list). The full PDF preview (utils/billPdf buildCreditNotePdfHtml —
// the same document the client receives) and its actions:
//   • Download PDF;
//   • Send credit note (SendBillModal with creditNote — the same consent rules
//     as a bill; the server renders + attaches the PDF, saves it to the case,
//     logs an Activity and records the first delivery);
//   • Mark as sent by post / handed over (MarkDeliveredModal with creditNote).
// Linked both ways: "Credits bill [number]" here goes back to the bill.
// A DRAFT credit note previews (watermarked) and can be issued (admins) or
// deleted, exactly as from the bill.
// (Batch 2) The preview is the shared DocumentPreviewFrame; the error box uses
// the casevo-danger tokens (light + dark).
// ============================================================
import { ErrorNote } from '../shared/ActionBox'
import React, { useEffect, useMemo, useState } from 'react'
import type { Bill, CaseCreditNoteRow, CreditNote } from '../../types/billing.types'
import { buildCreditNotePdfHtml, creditNoteHasWithheldAmounts, billHasWithheldAmounts, PDF_WITHHELD_NOTE, printCreditNotePdf } from '../../utils/billPdf'
import { deleteCreditNoteApi, issueCreditNoteApi, type BillSendSuccess } from '../../api/casevoApi'
import { useFirmLogos } from '../../utils/firmLogo'
import { useCasevoStore } from '../../store/useCasevoStore'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import SendBillModal from './SendBillModal'
import MarkDeliveredModal from './MarkDeliveredModal'
import DocumentPreviewFrame from '../shared/DocumentPreviewFrame'
import { emitCaseChange } from '../../utils/caseChangeEvents'
import { BIcon, B_PRIMARY, B_SECONDARY, B_ICON, CARD, EYEBROW, NOTE, errorText, fmtDate, money, moneyOrHidden } from './billingUi'

const DELIVERY_WORDS: Record<string, string> = { EMAIL: 'emailed', POST: 'sent by post', HAND: 'handed over' }
// Pure (exported for tests): "Sent to the client …" / "Not sent to the client yet".
export const creditNoteDeliveryText = (d: CreditNote['delivery']): string =>
  d ? `Sent to the client ${fmtDate(d.at, 'long')}${d.method ? ` (${DELIVERY_WORDS[d.method] ?? d.method.toLowerCase()})` : ''}${d.by ? ` by ${d.by}` : ''}` : 'Not sent to the client yet'

// ── Linked both ways ──
// On the bill: "£X credited: CN-0002, CN-0003" — each number opens that credit
// note. creditedPence null (hidden from this viewer) → "Credited: …" without £.
export const CreditedLinks: React.FC<{ creditNotes: CreditNote[]; creditedPence: number | null; onOpen: (id: string) => void }> = ({ creditNotes, creditedPence, onOpen }) => {
  const issued = creditNotes.filter((c) => c.status === 'ISSUED' && !!c.number)
  if (!issued.length) return null
  return (
    <span className="mt-1 text-casevo-forest">{creditedPence != null ? `${money(creditedPence)} credited: ` : 'Credited: '}
      {issued.map((c, i) => <React.Fragment key={c.id}>{i > 0 && ', '}<button type="button" className="font-mono font-semibold text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-[3px]" onClick={() => onOpen(c.id)}>{c.number}</button></React.Fragment>)}
    </span>
  )
}
// The case's bills list: each bill's credit notes, oldest first, listed under it.
export const creditNotesByBill = (rows: CaseCreditNoteRow[]): Map<string, CaseCreditNoteRow[]> => {
  const m = new Map<string, CaseCreditNoteRow[]>()
  for (const cn of [...rows].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())) m.set(cn.invoiceId, [...(m.get(cn.invoiceId) ?? []), cn])
  return m
}
// A credit note row's amount: a minus figure (hidden → the lock / dash).
export const creditRowAmount = (cn: Pick<CaseCreditNoteRow, 'amountPence'>): React.ReactNode => (cn.amountPence == null ? moneyOrHidden(null) : `−${money(cn.amountPence)}`)

// (Batch 2) The A4 page (860 × 1250) in the shared DocumentPreviewFrame, scaled
// to 0.72 (the bill's preview uses the same).
export const PREVIEW_PAGE_CLASS = 'w-[860px] h-[1250px] origin-top-left scale-[0.72] !border-0 !rounded-none'
const CreditNotePreview: React.FC<{ bill: Bill; cn: CreditNote }> = ({ bill, cn }) => {
  const logoV = useFirmLogos([cn.letterhead.firm.logoId])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const html = useMemo(() => buildCreditNotePdfHtml(bill, cn), [bill, cn, logoV])
  const scale = 0.72
  return (
    <div style={{ width: 860 * scale, height: 1250 * scale }} className="overflow-hidden rounded-[4px] shadow-[0_10px_30px_rgba(0,0,0,0.18)] bg-white shrink-0">
      <DocumentPreviewFrame title={`Credit note ${cn.number ?? 'draft'} preview`} html={html} className={PREVIEW_PAGE_CLASS} />
    </div>
  )
}

interface Props {
  bill: Bill
  cn: CreditNote
  backLabel: string
  onBack: () => void
  onOpenBill: () => void
  onChanged: (b: Bill) => void
}

const CreditNoteView: React.FC<Props> = ({ bill, cn, backLabel, onBack, onOpenBill, onChanged }) => {
  const isAdmin = useCasevoStore((s) => s.authUser?.role === 'admin')
  // (Audit batch 3) Deleting a draft: whoever drafted it, an admin or a bill approver (the server's rule).
  const meId = useCasevoStore((s) => s.authUser?.id)
  const isApproverHere = useCasevoStore((s) => s.authUser?.role === 'admin' || s.authUser?.effectivePermissions?.approveBills === true)
  const canDeleteDraft = (c: { createdByUserId?: string }): boolean => isApproverHere || (!!meId && c.createdByUserId === meId)
  const [err, setErr] = useState<string | null>(null)
  const [sendOpen, setSendOpen] = useState(false)
  const [postOpen, setPostOpen] = useState(false)
  const [confirm, setConfirm] = useState<null | 'issue' | 'delete'>(null)
  const [working, setWorking] = useState(false)
  const [note, setNote] = useState<{ text: string; warn: string | null } | null>(null)
  useEffect(() => { setNote(null); setErr(null) }, [cn.id])

  const issued = cn.status === 'ISSUED' && !!cn.number
  const withheld = creditNoteHasWithheldAmounts(cn) || billHasWithheldAmounts(bill)
  // The email + PDF carry the credit's figures — a viewer they're hidden from can't send it (server-enforced).
  // (Audit batch 3) Sending needs access to the case (bill.viewerCanEdit) — "View all billing" isn't enough.
  const offCase = bill.viewerCanEdit === false
  const canSend = issued && !withheld && !bill.moneyHidden && !bill.onAccountHidden && !offCase
  const pdf = (): void => { if (!printCreditNotePdf(bill, cn)) setErr('Your browser blocked the PDF tab — allow pop-ups for Casevo.') }
  const act = async (kind: 'issue' | 'delete'): Promise<void> => {
    setConfirm(null); setWorking(true); setErr(null)
    try {
      const b = kind === 'issue' ? await issueCreditNoteApi(cn.id) : await deleteCreditNoteApi(cn.id)
      onChanged(b)
      if (kind === 'delete') onOpenBill()
    } catch (e) { setErr(errorText(e, kind === 'issue' ? 'Couldn’t issue the credit note.' : 'Couldn’t delete the credit note.')) } finally { setWorking(false) }
  }
  const sent = (r: BillSendSuccess): void => {
    setSendOpen(false)
    if (r.bill) onChanged(r.bill)
    const warn = [
      !r.activityLogged && 'It couldn’t be logged on the case’s activity — add an entry by hand.',
      !r.deliveryRecorded && 'The delivery date couldn’t be recorded — please tell your administrator.',
      r.activityLogged && !r.pdfSaved && 'The sent PDF couldn’t be saved to the activity — please tell your administrator.',
    ].filter(Boolean).join(' ')
    setNote({ text: `${cn.number} sent to ${r.to.join(', ')}${r.cc.length ? ', with a copy to you' : ''}.${r.firstDelivery ? ' Recorded as delivered today.' : ''}`, warn: warn || null })
  }

  const billLink = (
    <button type="button" onClick={onOpenBill} className="font-mono font-semibold text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-[3px]">{bill.number ?? 'the bill'}</button>
  )
  return (
    <div>
      <div className="flex items-center gap-[7px] text-[12.5px] text-casevo-text-soft mb-3">
        <button type="button" className="font-medium text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-[3px]" onClick={onBack}>{backLabel}</button>
        <BIcon name="chevr" size={11} />
        <button type="button" className="font-mono hover:underline focus:outline-none casevo-focus rounded-[3px]" onClick={onOpenBill}>{bill.number ?? 'Bill'}</button>
        <BIcon name="chevr" size={11} /><span className={cn.number ? 'font-mono' : ''}>{cn.number ?? 'Draft credit note'}</span>
      </div>
      <div className="rounded-[14px] text-white relative overflow-hidden bg-gradient-to-br from-[#1a3a2a] to-[#2d6a4f] dark:from-[#1a4230] dark:to-[#2d6a4f] px-7 py-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-[6px] text-[11px] font-bold uppercase tracking-[0.08em] px-[10px] py-1 rounded-[7px] bg-white/20"><BIcon name="undo" size={13} />Credit note</span>
          <span className="inline-flex items-center text-[11px] font-semibold px-[10px] py-1 rounded-[7px] bg-white/10 font-mono">{cn.number ?? 'Number on issue'}</span>
          <span className="inline-flex items-center gap-[7px] text-[11px] font-bold uppercase tracking-[0.05em] pl-2 pr-[10px] py-1 rounded-full bg-white text-[#1f4a35]">
            <i className={`w-[7px] h-[7px] rounded-full ${issued ? 'bg-[#40916c]' : 'bg-[#c99a2e]'}`} />{issued ? 'Issued' : 'Draft'}
          </span>
        </div>
        <h1 className="mt-3 mb-[2px] text-[22px] font-bold tracking-[-0.01em] leading-tight">{cn.number ?? 'Draft credit note'} · −{moneyOrHidden(cn.amountPence, 18)}</h1>
        <div className="text-[13.5px] text-white/85">Credits bill <span className="font-mono">{bill.number}</span> · {bill.case.name} · {issued ? `issued ${fmtDate(cn.issuedAt, 'long')}` : `drafted ${fmtDate(cn.createdAt, 'long')}`}</div>
      </div>
      {err && <ErrorNote className="mt-3">{err}</ErrorNote>}
      <div className={`${CARD} flex items-center gap-3 px-4 py-3 mt-[14px] flex-wrap`}>
        <span className="w-[30px] h-[30px] rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center shrink-0"><BIcon name="memo" size={14} /></span>
        <div className="text-[13.5px] flex-1 min-w-[200px]">
          <b className="text-casevo-forest">Credit note PDF</b> <span className="text-casevo-text-soft">— {issued ? 'the exact document the client receives' : 'a preview; it’s numbered when issued'}.</span>
          {issued && <div className={`${NOTE} flex items-center gap-[5px] mt-[1px]`}>{cn.delivery && <BIcon name="check" size={12} className="text-casevo-brand dark:text-[#8fd6b0]" />}{creditNoteDeliveryText(cn.delivery)}</div>}
        </div>
        {withheld
          ? <span className={`${NOTE} inline-flex items-center gap-[5px]`} title={PDF_WITHHELD_NOTE}><BIcon name="lock" size={12} />PDF for billing admins</span>
          : <button type="button" className={B_SECONDARY} onClick={pdf}><BIcon name="dl" size={15} />Download PDF</button>}
        {issued && (canSend
          ? <>
            <button type="button" className={B_SECONDARY} onClick={() => { setNote(null); setPostOpen(true) }}><BIcon name="memo" size={14} />Sent by post / handed over</button>
            <button type="button" className={B_PRIMARY} onClick={() => { setNote(null); setSendOpen(true) }}><BIcon name="send" size={14} />{cn.delivery ? 'Send again' : 'Send credit note'}</button>
          </>
          : <span className={`${NOTE} inline-flex items-center gap-[6px]`}><BIcon name="lock" size={12} />{offCase ? 'Someone on this case sends this credit note.' : 'An admin or a colleague with “See colleagues’ billing” sends this credit note.'}</span>)}
        {!issued && <>
          {isAdmin
            ? <button type="button" className={B_PRIMARY} disabled={working} onClick={() => setConfirm('issue')}><BIcon name="lock" size={14} />{working ? 'Working…' : 'Issue credit note'}</button>
            : <span className={`${NOTE} inline-flex items-center gap-[5px]`}><BIcon name="clock" size={12} />Awaiting an admin to issue</span>}
          {canDeleteDraft(cn) && <button type="button" className={`${B_SECONDARY} !text-casevo-danger`} disabled={working} onClick={() => setConfirm('delete')}><BIcon name="trash" size={14} />Delete draft</button>}
        </>}
      </div>
      {note && (
        <div role="status" className="mt-[10px] rounded-[10px] px-3 py-2 text-[13px] bg-casevo-mint text-casevo-forest flex gap-2 items-start">
          <BIcon name="check" size={14} className="mt-[2px] text-casevo-brand dark:text-[#8fd6b0] shrink-0" />
          <span className="flex-1">{note.text}{note.warn && <span className="block text-casevo-warn">{note.warn}</span>}</span>
          <button type="button" className={B_ICON} aria-label="Dismiss" onClick={() => setNote(null)}><BIcon name="x" size={13} /></button>
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_330px] gap-[18px] mt-[14px] items-start">
        <div className={`${CARD} p-[22px] bg-casevo-input flex justify-center overflow-hidden`}>
          {withheld
            ? <div className="py-10 px-4 text-center max-w-[360px]"><span className="inline-flex w-9 h-9 rounded-[10px] bg-casevo-surface text-casevo-text-soft items-center justify-center"><BIcon name="lock" size={15} /></span>
              <div className="mt-2 text-[13.5px] font-semibold text-casevo-forest">Preview unavailable</div>
              <div className={`${NOTE} mt-1`}>{PDF_WITHHELD_NOTE}</div></div>
            : <CreditNotePreview bill={bill} cn={cn} />}
        </div>
        <div className="flex flex-col gap-3">
          <div className={`${CARD} px-[18px] py-4`}>
            <div className={EYEBROW}>Amount credited</div>
            <div className="text-[28px] font-semibold tracking-[-0.015em] mt-[2px] tabular-nums text-casevo-forest">−{moneyOrHidden(cn.amountPence, 20)}</div>
            <div className={NOTE}>net {moneyOrHidden(cn.netPence, 11)} + VAT {moneyOrHidden(cn.vatPence, 11)}</div>
            <dl className="m-0 mt-3 grid grid-cols-[110px_1fr] gap-x-3 gap-y-[6px] text-[13px]">
              <dt className="text-casevo-text-soft">Credits bill</dt><dd className="m-0">{billLink}</dd>
              <dt className="text-casevo-text-soft">Reason</dt><dd className="m-0 text-casevo-forest">“{cn.reason}”</dd>
              <dt className="text-casevo-text-soft">{issued ? 'Issued' : 'Drafted'}</dt><dd className="m-0 text-casevo-forest">{fmtDate(issued ? cn.issuedAt : cn.createdAt, 'long')}{cn.createdBy ? ` · raised by ${cn.createdBy}` : ''}</dd>
              {cn.letterhead.signatory?.name && <><dt className="text-casevo-text-soft">Signed by</dt><dd className="m-0 text-casevo-forest">{cn.letterhead.signatory.name}{cn.letterhead.signatory.title ? `, ${cn.letterhead.signatory.title}` : ''}</dd></>}
              {cn.releaseToUnbilled && <><dt className="text-casevo-text-soft">Release</dt><dd className="m-0 text-casevo-forest">Fully-credited items go back to unbilled</dd></>}
            </dl>
          </div>
        </div>
      </div>
      <ConfirmDialogue isOpen={confirm === 'issue'} title="Issue this credit note?" message={`It takes the next credit note number and reduces what’s owed on ${bill.number}. It can’t be changed or deleted afterwards.`} confirmLabel="Issue credit note"
        onCancel={() => setConfirm(null)} onConfirm={() => { void act('issue') }} />
      <ConfirmDialogue isOpen={confirm === 'delete'} title="Delete this draft credit note?" message="It hasn’t been issued, so nothing changes on the bill." confirmLabel="Delete"
        onCancel={() => setConfirm(null)} onConfirm={() => { void act('delete') }} />
      {postOpen && <MarkDeliveredModal bill={bill} creditNote={cn} onClose={() => setPostOpen(false)} onDone={(b) => {
        setPostOpen(false)
        onChanged(b)
        if (bill.case?.id) emitCaseChange(bill.case.id, 'bill')
        const fresh = b.creditNotes.find((c) => c.id === cn.id)
        setNote({ text: `${cn.number}: delivery recorded${fresh?.delivery ? ` (delivered ${fmtDate(fresh.delivery.at, 'long')})` : ''}.`, warn: null })
      }} />}
      {sendOpen && <SendBillModal bill={bill} creditNote={cn} onClose={() => setSendOpen(false)} onUsePost={() => { setSendOpen(false); setPostOpen(true) }} onSent={sent} />}
    </div>
  )
}

export default CreditNoteView
